import { cloneDeep, isEqual } from "lodash";

type SaveNoteValues<T> = (values: T) => Promise<boolean>;

export interface AiSessionNoteReviewSaveCoordinator<T> {
    prepareReview(values: T, saveValues: SaveNoteValues<T>): Promise<boolean>;
    reset(values: T): void;
    save(values: T, saveValues: SaveNoteValues<T>): Promise<boolean>;
    synchronizeSavedValues(values: T): void;
}

export function createAiSessionNoteReviewSaveCoordinator<T>(
    initialValues: T
): AiSessionNoteReviewSaveCoordinator<T> {
    let lastSavedValues = cloneDeep(initialValues);
    let inFlightSave: { values: T; promise: Promise<boolean> } | null = null;
    let scopeVersion = 0;

    const save = (values: T, saveValues: SaveNoteValues<T>) => {
        const valuesToSave = cloneDeep(values);
        if (inFlightSave && isEqual(inFlightSave.values, valuesToSave)) {
            return inFlightSave.promise;
        }

        const saveScopeVersion = scopeVersion;
        const promise = saveValues(valuesToSave)
            .then((didSave) => {
                if (didSave && saveScopeVersion === scopeVersion) {
                    lastSavedValues = cloneDeep(valuesToSave);
                }
                return didSave;
            })
            .finally(() => {
                if (inFlightSave?.promise === promise) {
                    inFlightSave = null;
                }
            });
        inFlightSave = { values: valuesToSave, promise };
        return promise;
    };

    return {
        prepareReview(values, saveValues) {
            const currentValues = cloneDeep(values);
            if (!inFlightSave && isEqual(lastSavedValues, currentValues)) {
                return Promise.resolve(true);
            }
            return save(currentValues, saveValues);
        },
        reset(values) {
            scopeVersion += 1;
            lastSavedValues = cloneDeep(values);
            inFlightSave = null;
        },
        save,
        synchronizeSavedValues(values) {
            if (!inFlightSave) {
                lastSavedValues = cloneDeep(values);
            }
        },
    };
}
